import { EMPTY_ARRAY } from '@modules/common/constants';
import {
  useIsUnifiedFieldsEnabled,
  useShouldRemoveFileUploadPrefix,
} from '@modules/common/hooks';
import { ShowFeedbackDrawerFromTableOptions } from '@modules/quantify/components/QuantifyReportTableWidget/types';
import {
  DEFAULT_COMPARE_DURATION_A_NAME,
  DEFAULT_COMPARE_DURATION_B_NAME,
  DEFAULT_COMPARE_FILTER_A_NAME,
  DEFAULT_COMPARE_FILTER_B_NAME,
} from '@modules/quantify/constants';
import { useFetchTotalRecordCountForCompareFields } from '@modules/quantify/hooks/useFetchTotalRecordCountForCompareFields';
import { useGetCompareColors } from '@modules/quantify/hooks/useGetCompareColors';
import {
  getCompareColumns,
  getLabelColumn,
} from '@modules/quantify/hooks/useGetQuantifyReportTableColumns/helpers';
import { getActionsColumn } from '@modules/quantify/hooks/useGetQuantifyReportTableColumns/helpers/getActionsColumn';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { QuantifyQuery } from '@modules/quantify/store/types';
import { getCompareLabelPrefix } from '@modules/quantify/utils';
import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useIsSingleRecordQuery } from '../useIsSingleRecordQuery';

interface useGetCompareAnalysisTableColumnsParams {
  onFeedbackCountClick: (params: ShowFeedbackDrawerFromTableOptions) => void;
  shouldHideActions?: boolean;
  // To support different formula types, irrespective of the store value
  formulaID: QuantifyQuery['formulaID'];
  isCompactTableVariant?: boolean;
  shouldShowAddToFilterForBothCompareFields?: boolean;
  shouldShowCompareFieldHeaderTooltip?: boolean;
  shouldShowMergeWithSelectedReasonsAction?: boolean;
}

export function useGetCompareAnalysisTableColumns({
  onFeedbackCountClick,
  shouldHideActions,
  formulaID,
  isCompactTableVariant,
  shouldShowAddToFilterForBothCompareFields,
  shouldShowCompareFieldHeaderTooltip,
  shouldShowMergeWithSelectedReasonsAction = true,
}: useGetCompareAnalysisTableColumnsParams): {
  columns: GridColDef[];
  columnGroupingModel: GridColumnGroupingModel;
} {
  const measuredAsCriteria = useQuantifyStore(
    ({ query }) => query.measuredAsCriteria,
  );
  const groupBy = useQuantifyStore(({ query }) => query.groupBy);
  const compareTo = useQuantifyStore(({ query }) => query.compareTo);
  const { totalFeedbackCount } = useFetchTotalRecordCountForCompareFields({
    shouldSkip: isCompactTableVariant,
  });
  const compareColors = useGetCompareColors();
  const compareFilterAName = useQuantifyStore(
    ({ metadata }) =>
      metadata.compareQBField.compareQBFieldAName ||
      DEFAULT_COMPARE_FILTER_A_NAME,
  );
  const compareFilterBName = useQuantifyStore(
    ({ metadata }) =>
      metadata.compareQBField.compareQBFieldBName ||
      DEFAULT_COMPARE_FILTER_B_NAME,
  );
  const compareDurationAName = useQuantifyStore(
    ({ metadata }) =>
      metadata.compareDurationField.compareDurationAName ||
      DEFAULT_COMPARE_DURATION_A_NAME,
  );
  const compareDurationBName = useQuantifyStore(
    ({ metadata }) =>
      metadata.compareDurationField.compareDurationBName ||
      DEFAULT_COMPARE_DURATION_B_NAME,
  );

  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();
  const isUnifiedFieldEnabled = useIsUnifiedFieldsEnabled();
  const isSingleRecordQuery = useIsSingleRecordQuery({ formulaID });

  const compareDataPoints =
    useQuantifyStore(({ reportData }) => reportData.compare.tableRows) ??
    EMPTY_ARRAY;

  const hasCoOccurringDataPointInData = compareDataPoints.some(
    (tableRow) => !tableRow.isRelevant,
  );

  const columns = useMemo(() => {
    if (!compareTo) {
      return { columnGroupingModel: [], columns: [] };
    }

    const labelColumn = isSingleRecordQuery
      ? null
      : getLabelColumn({
          groupBy,
          isCompactTableVariant,
          hasCoOccurringDataPointInData,
          shouldRemoveFileUploadPrefix,
          isUnifiedFieldEnabled,
        });

    const actionsColumn = getActionsColumn({
      shouldHideActions,
      isCompactTableVariant,
      shouldShowAddToFilterForBothCompareFields,
      shouldShowMergeWithSelectedReasonsAction,
    });

    const compareLabelPrefix = getCompareLabelPrefix({
      formulaID,
      measuredAsCriteria,
    });

    const columns = getCompareColumns({
      compareTo,
      groupBy,
      onFeedbackCountClick,
      colors: compareColors,
      totalFeedbackCount,
      compareLabelPrefix,
      compareFieldNames: {
        fieldAName:
          compareTo?.type === 'whereClause'
            ? compareFilterAName
            : compareDurationAName,
        fieldBName:
          compareTo?.type === 'whereClause'
            ? compareFilterBName
            : compareDurationBName,
      },
      formulaID,
      shouldShowCompareFieldHeaderTooltip,
      isCompactTableVariant,
      measuredAsCriteria,
      shouldRemoveFileUploadPrefix,
      isUnifiedFieldEnabled,
      isSingleRecordQuery,
    }).map((column) => {
      const updatedColumn = {
        ...column,
        flex: 1,
        width: undefined,
      };
      return isCompactTableVariant
        ? { ...updatedColumn, sortable: false }
        : updatedColumn;
    });

    return {
      columns: [labelColumn, ...columns, actionsColumn].filter(
        (item): item is GridColDef => !!item,
      ),
      columnGroupingModel: [],
    };
  }, [
    compareColors,
    compareDurationAName,
    compareDurationBName,
    compareFilterAName,
    compareFilterBName,
    compareTo,
    formulaID,
    isSingleRecordQuery,
    groupBy,
    hasCoOccurringDataPointInData,
    isCompactTableVariant,
    measuredAsCriteria,
    onFeedbackCountClick,
    shouldHideActions,
    shouldShowAddToFilterForBothCompareFields,
    shouldShowCompareFieldHeaderTooltip,
    totalFeedbackCount,
    shouldRemoveFileUploadPrefix,
    isUnifiedFieldEnabled,
    shouldShowMergeWithSelectedReasonsAction,
  ]);

  return columns;
}
